import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Link,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import { PrimaryButton } from '../../../components/ui/button';
import datavLogo from '../../../Images/datav_logo.png';

const PricingNavbar = ({
  hideLoginButton = false,
  enableScrollEffect = true,
  customStyles = {},
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  useEffect(() => {
    if (!enableScrollEffect) return;

    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [enableScrollEffect]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const navItems = ['Product', 'Solutions', 'Pricing', 'Docs'];

  const handleNavClick = (item) => {
    // Handle navigation for new menu items
    if (item === 'Product') {
      // Scroll to products section or navigate
      navigate('/');
      setTimeout(() => {
        const productsSection = document.getElementById('products');
        if (productsSection) {
          productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else if (item === 'Solutions') {
      navigate('/');
      setTimeout(() => {
        const solutionsSection = document.getElementById('solutions');
        if (solutionsSection) {
          solutionsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else if (item === 'Pricing') {
      navigate('/pricing');
    } else if (item === 'Docs') {
      navigate('/documentation');
    }
  };

  const getAppBarStyles = () => ({
    backgroundColor: 'transparent',
    background: enableScrollEffect && scrolled
      ? 'rgba(255, 255, 255, 0.95)'
      : 'transparent',
    backdropFilter: enableScrollEffect && scrolled ? 'blur(20px)' : 'none',
    transition: 'all 0.3s ease',
    boxShadow: enableScrollEffect && scrolled ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none',
    border: 'none',
    ...customStyles.appBar,
  });

  const getToolbarStyles = () => ({
    justifyContent: 'space-between',
    background: 'transparent',
    px: { xs: 2, sm: 3, md: 4, lg: 6 },
    py: { xs: 1.5, sm: 2, md: 3 },
    ...customStyles.toolbar,
  });

  return (
    <>
      <AppBar position="fixed" sx={getAppBarStyles()}>
        <Toolbar sx={getToolbarStyles()}>
          {/* Logo Section - Left */}
          <Box
            onClick={() => navigate('/')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {/* Logo Image */}
            <Box
              component="img"
              src={datavLogo}
              alt="Dataverse Logo"
              sx={{
                cursor: 'pointer',
                height: 'auto',
                maxWidth: { xs: '90px', sm: '115px', md: '135px' },
                width: { xs: '54px', sm: '72px', md: '85px' },
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>

          {/* Desktop Navigation - Centered */}
          {!isMobile && (
            <Box
              sx={{
                display: 'flex',
                gap: { md: 3, lg: 4 },
                alignItems: 'center',
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                ...customStyles.navigationBox,
              }}
            >
              {navItems.map((item) => (
                <Link
                  key={item}
                  variant="body1"
                  onClick={() => handleNavClick(item)}
                  sx={{
                    color: '#4A5565',
                    textTransform: 'none',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: { md: '0.9rem', lg: '1rem' },
                    letterSpacing: '0.01em',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      color: '#2D3748',
                    },
                    ...customStyles.navLink,
                  }}
                >
                  {item}
                </Link>
              ))}
            </Box>
          )}

          {/* Desktop CTA Buttons - Right */}
          {!isMobile && !hideLoginButton && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexShrink: 0 }}>
              <Link
                onClick={() => navigate('/login')}
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '-0.31px',
                  textAlign: 'center',
                  color: '#4A5565',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: '#2D3748',
                  },
                }}
              >
                Sign In
              </Link>
              <PrimaryButton
                variant="contained"
                onClick={() => navigate('/trial')}
                sx={customStyles.ctaButton}
              >
                Get Started
              </PrimaryButton>
            </Box>
          )}

          {/* Mobile Menu Icon */}
          {isMobile && (
            <IconButton
              sx={{
                color: '#4A5565',
                background: 'rgba(74, 85, 101, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(74, 85, 101, 0.2)',
                '&:hover': {
                  background: 'rgba(74, 85, 101, 0.2)',
                },
              }}
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Drawer 
        anchor="right" 
        open={mobileOpen} 
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            width: { xs: '70%', sm: '60%' },
            maxWidth: '320px',
            background: '#FFFFFF',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            py: { xs: 4, sm: 5 },
            px: { xs: 2, sm: 3 },
            color: '#4A5565',
          }}
        >
          <List sx={{ flex: 1, pt: 2 }}>
            {navItems.map((item) => (
              <ListItem
                key={item}
                onClick={() => {
                  handleNavClick(item);
                  handleDrawerToggle();
                }}
                sx={{
                  py: { xs: 2, sm: 2.5 },
                  px: { xs: 2, sm: 2.5 },
                  mb: 0.5,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(74, 85, 101, 0.08)',
                  },
                }}
              >
                <ListItemText
                  primary={item}
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: '#4A5565',
                      fontWeight: 600,
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      letterSpacing: '0.01em',
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
          
          {!hideLoginButton && (
            <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  navigate('/login');
                  handleDrawerToggle();
                }}
                sx={{
                  width: '100%',
                  py: { xs: 1.25, sm: 1.5 },
                  borderRadius: '8px',
                  borderColor: 'rgba(74, 85, 101, 0.2)',
                  backgroundColor: 'transparent',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '16px',
                  lineHeight: '24px',
                  letterSpacing: '-0.31px',
                  textAlign: 'center',
                  color: '#4A5565',
                  textTransform: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(74, 85, 101, 0.05)',
                    borderColor: 'rgba(74, 85, 101, 0.3)',
                  },
                }}
              >
                Sign In
              </Button>
              <PrimaryButton
                variant="contained"
                onClick={() => {
                  navigate('/trial');
                  handleDrawerToggle();
                }}
                sx={{
                  width: '100%',
                }}
              >
                Get Started
              </PrimaryButton>
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
};

PricingNavbar.propTypes = {
  hideLoginButton: PropTypes.bool,
  enableScrollEffect: PropTypes.bool,
  customStyles: PropTypes.object,
};

export default PricingNavbar;
